import { useMemo, type ReactNode } from 'react';
import { useLoaderData } from 'react-router';
import { EnquiryFormPageType } from 'generated/graphql';

import {
    PageCtasProvider,
    type PageBookIntentBase,
    type PageCtasProviderProps,
} from '~/modules/enquiry/component/PageCtasProvider';
import { CtaAutoOpen } from '~/modules/enquiry/component/CtaAutoOpen';
import type { EnquiryModalContext } from '~/modules/enquiry/hooks/use-enquiry-modal';
import type { loader } from '~/routes/_app.product.$urlSegment';

/**
 * Resolves the product page's Enquire and Book a meeting buttons once, from
 * the loader's `pageCtas` (null keeps the Product Call-to-Action wording and
 * the professional's enquiry audience), for every product surface to read
 * through `useEnquireCta` / `useBookCta`.
 *
 * The enquiry context carries the professional (branch options, custom
 * message settings), the product's title and images; the booking intent
 * carries the practice and the product the meeting is about. Each surface adds
 * its own source and, where it has one, the selected variant.
 */
export const ProductPageCtasProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { product, branchOptions, primaryCTA, pageCtas } =
        useLoaderData<typeof loader>();
    const professional = product.Professional;
    const firstImage = product.LibraryImages?.[0]?.LibraryImageFile;
    // The gate renders this across a 478px pane, so it wants the detail-page
    // rendition the page itself uses; PhotoTile is sized for a grid tile and
    // goes soft at that width.
    const paneImage = firstImage?.DetailPageSquare?.Desktop ?? undefined;
    const enquireLabel = pageCtas?.enquire.usesForms
        ? pageCtas.enquire.label
        : primaryCTA.primaryCTALabelTitleCase;

    const fallback = useMemo<PageCtasProviderProps['fallback']>(
        () => ({
            pageType: EnquiryFormPageType.Product,
            enquiryAudience: professional.EnquiryAudience,
            enquireLabel: primaryCTA.primaryCTALabelTitleCase,
            enquireLabelShort: primaryCTA.primaryCTALabelShort,
            enquireLabelEnum: primaryCTA.primaryCTA,
        }),
        [professional.EnquiryAudience, primaryCTA],
    );

    const enquiry = useMemo<EnquiryModalContext>(
        () => ({
            professional: {
                ID: professional.ID,
                Title: professional.Title,
                branchOptions,
                CompanyLogo: professional.CompanyLogo ?? '',
                LogoBackgroundColor:
                    professional.LogoBackgroundColor ?? 'white',
                CustomEnquiryMessage:
                    professional.CustomEnquiryMessage ?? undefined,
                // The professional's "write your own message" setting: the
                // box starts empty, with their hint as the placeholder.
                EnquiryMessageOverrideEnabled:
                    professional.EnquiryMessageOverrideEnabled ?? undefined,
                EnquiryMessagePlaceholder:
                    professional.EnquiryMessagePlaceholder || undefined,
            },
            itemType: 'product',
            siteTreeID: product.ID,
            ctaLabel: enquireLabel,
            includeBranch: branchOptions.length > 0,
            itemTitle: product.Title,
            itemImage: firstImage?.PhotoTile ?? undefined,
            brandImage: paneImage,
        }),
        [
            professional,
            branchOptions,
            product.ID,
            product.Title,
            enquireLabel,
            firstImage,
            paneImage,
        ],
    );

    const book = useMemo<PageBookIntentBase>(
        () => ({
            professionalUrlSegment: professional.URLSegment,
            siteTreeId: product.ID,
            professionalId: professional.ID,
            itemTitle: product.Title,
            professionalName: professional.Title,
            companyLogo: professional.CompanyLogo,
            logoBackgroundColor: professional.LogoBackgroundColor,
            ...(paneImage && { paneImage }),
        }),
        [professional, product.ID, product.Title, paneImage],
    );

    return (
        <PageCtasProvider
            loaded={pageCtas}
            fallback={fallback}
            enquiry={enquiry}
            book={book}
        >
            {children}
            <CtaAutoOpen />
        </PageCtasProvider>
    );
};
